/**
 * 功放相关：
 * - MQTT 主题段（server/device/{id}/…、amplifier/device/{id}/…）对 deviceTypeId=9 与后端约定使用
 *   GET /devices/my-session 返回项中的 16 进制 **snNumber**。
 * - `remote_control.data.device_id` 由后端转发为十进制，需与 my-session 的 **serialNumber** 比较。
 * - /amplifier/connect、/amplifier/listen 的响应解析仍用于手动连接等场景。
 *
 * 成功示例（connect/listen）：
 * `{ "success": true, "message": "连接成功", "data": { "deviceId": 42, "userId": 1001, "serialNumber": 279854551 }, "timestamp": ... }`
 * （`data` 内 `deviceId` / `userId` / `serialNumber` 可为 number，解析时统一为 string / number）
 *
 * 失败示例：
 * `{ "success": false, "message": "设备不存在，无法连接", "data": null, "timestamp": ... }`
 */
import { i18nService } from '../i18n/i18nService';

/** 后端 connect / listen 成功响应中 `data` 的字段约定 */
export interface AmplifierConnectResponseData {
  deviceId?: string | number;
  userId?: string | number;
  serialNumber?: string | number;
}

/**
 * 从 my-session 会话设备项取功放 MQTT 主题标识（16 进制 snNumber）。
 * deviceTypeId 必须为 9；否则返回 null。
 */
export function mqttAmplifierTopicIdFromSessionDevice(device: {
  deviceTypeId?: number;
  serialNumber?: string | number | null;
  snNumber?: string | null;
}): string | null {
  if (device.deviceTypeId !== 9) return null;
  const snNumber = device.snNumber;
  if (typeof snNumber !== 'string') return null;
  const trimmed = snNumber.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function pickDeviceIdFromUnknown(v: unknown): string | undefined {
  if (typeof v === 'string' && v.trim().length > 0) return v.trim();
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  return undefined;
}

function pickUserIdFromUnknown(v: unknown): number | undefined {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string' && v.trim().length > 0) {
    const n = Number(v.trim());
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

function readAmplifierSessionFields(rec: Record<string, unknown>): { deviceId?: string; userId?: number } {
  let deviceId =
    pickDeviceIdFromUnknown(rec.deviceId) ?? pickDeviceIdFromUnknown(rec.device_id);
  if (!deviceId) {
    deviceId =
      pickDeviceIdFromUnknown(rec.serialNumber) ?? pickDeviceIdFromUnknown(rec.serial_number);
  }
  const userId = pickUserIdFromUnknown(rec.userId) ?? pickUserIdFromUnknown(rec.user_id);
  return { deviceId, userId };
}

/**
 * 从 connect / listen 响应 JSON 中解析 deviceId、userId。
 * 兼容 `data` 嵌套、顶层字段、snake_case；`deviceId`/`userId`/`serialNumber` 可为数字；
 * 若无 `deviceId` 可回落到 `serialNumber`（字符串形式的设备侧序列号/编号）。
 */
export function extractAmplifierSessionFromResponse(body: unknown): { deviceId: string; userId: number } | null {
  if (body == null || typeof body !== 'object') return null;
  const o = body as Record<string, unknown>;

  let deviceId: string | undefined;
  let userId: number | undefined;

  const nested = o.data;
  if (nested != null && typeof nested === 'object' && !Array.isArray(nested)) {
    const inner = readAmplifierSessionFields(nested as Record<string, unknown>);
    deviceId = inner.deviceId;
    userId = inner.userId;
  }

  const top = readAmplifierSessionFields(o);
  if (!deviceId) deviceId = top.deviceId;
  if (userId == null) userId = top.userId;

  if (!deviceId || userId == null) return null;
  return { deviceId, userId };
}

/**
 * 连接/监听功放：优先从响应体解析 deviceId、userId；若后端仅返回 success 且 data 为空（如 data: null），
 * 用功放请求路径中的 ID（用户输入的唯一 ID 或会话里的序列号）与当前用户 id 兜底。
 */
export function resolveAmplifierSessionOrFallback(
  body: unknown,
  fallbackDeviceId: string,
  fallbackUserId: number = 0
): { deviceId: string; userId: number } | null {
  const parsed = extractAmplifierSessionFromResponse(body);
  if (parsed) return parsed;

  if (body == null || typeof body !== 'object') return null;
  if ((body as { success?: boolean }).success !== true) return null;

  const trimmed = (fallbackDeviceId || '').trim();
  if (!trimmed) return null;

  const uid =
    typeof fallbackUserId === 'number' && Number.isFinite(fallbackUserId) ? fallbackUserId : 0;
  return { deviceId: trimmed, userId: uid };
}

export function isAmplifierApiExplicitFailure(body: unknown): boolean {
  if (body == null || typeof body !== 'object') return false;
  return (body as { success?: boolean }).success === false;
}

export function getAmplifierApiFailureMessage(body: unknown): string | undefined {
  if (body == null || typeof body !== 'object') return undefined;
  const o = body as Record<string, unknown>;
  const direct = [o.message, o.msg, o.errorMsg, o.errorMessage, o.error_description];
  for (const c of direct) {
    if (typeof c === 'string' && c.trim().length > 0) return c.trim();
  }
  const nested = o.data;
  if (nested != null && typeof nested === 'object') {
    const d = nested as Record<string, unknown>;
    const dm = [d.message, d.msg, d.errorMsg];
    for (const c of dm) {
      if (typeof c === 'string' && c.trim().length > 0) return c.trim();
    }
  }
  return undefined;
}

/** ApiClient 抛出的 HTTP 错误等在开发与生产下的可读文案 */
export function amplifierConnectErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  const anyErr = error as { data?: { message?: string; msg?: string } } | undefined;
  const fromData =
    anyErr?.data &&
    (typeof anyErr.data.message === 'string'
      ? anyErr.data.message
      : typeof anyErr.data.msg === 'string'
        ? anyErr.data.msg
        : '');
  if (fromData) return fromData;
  return String(error ?? i18nService.t('common.requestFailed'));
}
